<template>
  <v-container fluid>
    <v-card class="pa-4 pt-3">
      <!-- Encabezado -->
      <v-row class="ma-0 mb-1">
        <v-btn class="mr-4 my-2" density="compact" variant="text" @click="goToToday">
          <v-icon class="mr-2">mdi-calendar-import-outline</v-icon>
          Hoy
        </v-btn>
        <v-btn class="pt-2 mx-1" icon variant="text" density="compact" @click="goToPrevious">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn class="pt-2 mx-1" icon variant="text" density="compact" @click="goToNext">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>

        <v-select
          v-if="showMonthSelect"
          class="month_select ml-2"
          v-model="selectedMonth"
          :items="months"
          item-title="title"
          item-value="value"
          density="compact"
          variant="solo"
          hide-details
          max-width="170"
        />
        <v-select
          v-if="showYearSelect"
          class="month_select"
          v-model="selectedYear"
          :items="years"
          density="compact"
          hide-details
          max-width="120"
          variant="solo"
        />
        <v-spacer />
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" style="margin:2px;" class="elevation-0">
              <v-icon class="mr-2">mdi-calendar-week</v-icon>
              {{ calendarMode }}
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-for="(mode, index) in calendarModes" :key="index" @click="calendarMode = mode">
              <v-list-item-title>{{ mode }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-row>

      <!-- Calendario dinámico -->
      <component
        :is="activeViewComponent"
        :base-date="baseDate"
        :events="mappedEvents"
        :calendar-config="calendarConfig"
        :on-create="openCreateForm"
        :on-view="openDetailDrawer"
        :title="props.title"
        :module-name="props.moduleName"
      />
    </v-card>

    <!-- Drawer de detalle -->
    <v-navigation-drawer class="float-drawer" v-model="drawerVisible" location="right" width="500" temporary>
      <v-btn @click="drawerVisible=false"><v-icon>mdi-chevron-left</v-icon></v-btn>
      <component :is="drawerComponent" v-if="drawerComponent" :id="selectedEventId" />
    </v-navigation-drawer>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import {
  startOfToday,
  addDays,
  addMonths,
  addYears,
  subDays,
  subMonths,
  subYears,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  format
} from 'date-fns';

import CalendarMonth from './CalendarMonth.vue';
import CalendarWeek from './CalendarWeek.vue';
import CalendarDay from './CalendarDay.vue';
import CalendarYear from './CalendarYear.vue';

import { useActivitiesStore } from '@/store/activities';
import { configRegistry } from '@/lib/configRegistry';
import { useFloatFormStore } from '@/store/floatForm';

const { openForm } = useFloatFormStore();

const props = defineProps<{
  moduleName: string;
  title: { singular: string };
}>();

const calendarModes = ['Día', 'Semana', 'Mes', 'Año'];
const calendarMode = ref<'Día' | 'Semana' | 'Mes' | 'Año'>('Mes');

const baseDate = ref(startOfToday());
const selectedMonth = ref(baseDate.value.getMonth());
const selectedYear = ref(baseDate.value.getFullYear());

const months = Array.from({ length: 12 }, (_, i) => ({
  title: new Date(2000, i).toLocaleString('default', { month: 'long' }),
  value: i,
}));
const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

watch([selectedMonth, selectedYear], () => {
  baseDate.value = new Date(selectedYear.value, selectedMonth.value);
});

const activeViewComponent = computed(() => {
  switch (calendarMode.value) {
    case 'Día': return CalendarDay;
    case 'Semana': return CalendarWeek;
    case 'Mes': return CalendarMonth;
    case 'Año': return CalendarYear;
  }
});

function goToToday() {
  const today = startOfToday();
  baseDate.value = today;
  selectedMonth.value = today.getMonth();
  selectedYear.value = today.getFullYear();
}

function goToNext() {
  switch (calendarMode.value) {
    case 'Día': baseDate.value = addDays(baseDate.value, 1); break;
    case 'Semana': baseDate.value = addDays(baseDate.value, 7); break;
    case 'Mes': baseDate.value = addMonths(baseDate.value, 1); selectedMonth.value = baseDate.value.getMonth(); selectedYear.value = baseDate.value.getFullYear(); break;
    case 'Año': baseDate.value = addYears(baseDate.value, 1); break;
  }
}

function goToPrevious() {
  switch (calendarMode.value) {
    case 'Día': baseDate.value = subDays(baseDate.value, 1); break;
    case 'Semana': baseDate.value = subDays(baseDate.value, 7); break;
    case 'Mes': baseDate.value = subMonths(baseDate.value, 1); selectedMonth.value = baseDate.value.getMonth(); selectedYear.value = baseDate.value.getFullYear(); break;
    case 'Año': baseDate.value = subYears(baseDate.value, 1); break;
  }
}

const showMonthSelect = computed(() => calendarMode.value === 'Mes');
const showYearSelect = computed(() => calendarMode.value !== 'Día');

const store = useActivitiesStore();
const rawEvents = computed(() => store.activities);

const calendarConfig = ref<{ dateField: string; filterField: string; labelField: string; colorField?: string }>({ dateField: 'date', filterField: 'date_between', labelField: 'name' });

const drawerVisible = ref(false);
const drawerComponent = ref<any>(null);
const selectedEventId = ref<number | null>(null);

async function openDetailDrawer(event: any) {
  try {
    const loader = configRegistry[props.moduleName];
    if (!loader) throw new Error('No config');
    const mod = await loader();
    if (mod.DETAIL?.component) {
      drawerComponent.value = (await mod.DETAIL.component()).default;
      selectedEventId.value = event.id;
      drawerVisible.value = true;
    } else {
      console.warn(`[Calendar] No se encontró DETAIL.component en config para módulo ${props.moduleName}`);
    }
  } catch (err) {
    console.error('[Calendar] Error cargando componente de detalle:', err);
  }
}

(async () => {
  try {
    const loader = configRegistry[props.moduleName];
    if (!loader) throw new Error('No config');
    const mod = await loader();
    if (mod.CALENDAR_CONFIG) calendarConfig.value = mod.CALENDAR_CONFIG;
  } catch (err) {
    console.warn('[CalendarContainer] No se pudo cargar CALENDAR_CONFIG:', err);
  }
})();

const mappedEvents = computed(() => {
  const { dateField, labelField, colorField } = calendarConfig.value;
  return rawEvents.value.map(event => {
    const date = getValueByPath(event, dateField);
    const label = getValueByPath(event, labelField);
    const color = colorField ? getValueByPath(event, colorField) : 'primary';
    return { id: event.id, date, name: label, color, original: event };
  });
});

function getValueByPath(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

function openCreateForm(data: { date: string | [string, string] }) {
  openForm({
    component: 'CreateForm',
    formTitle: 'Crear ' + props.title?.singular,
    formWidth: '1080',
    module: props.moduleName,
    props: {
      initialValues: {
        date: data.date
      }
    }
  });
}

const dateRange = computed(() => {
  const field = calendarConfig.value.filterField || 'date';
  let start: Date, end: Date;

  switch (calendarMode.value) {
    case 'Día':
      start = baseDate.value;
      end = baseDate.value;
      break;
    case 'Semana':
      start = startOfWeek(baseDate.value, { weekStartsOn: 1 });
      end = endOfWeek(baseDate.value, { weekStartsOn: 1 });
      break;
    case 'Mes':
    default:
      start = startOfWeek(startOfMonth(baseDate.value), { weekStartsOn: 1 });
      end = endOfWeek(endOfMonth(baseDate.value), { weekStartsOn: 1 });
      break;
  }

  return {
    [`filter[${field}]`]: `${format(start, 'yyyy-MM-dd')},${format(end, 'yyyy-MM-dd')}`
  };
});

watch(
  [dateRange, calendarConfig],
  async () => {
    try {
      await store.fetchActivities(dateRange.value);
    } catch (err) {
      console.error('[CalendarContainer] Error fetching activities:', err);
    }
  },
  { immediate: true }
);
</script>

<style>
.month_select .v-field {
  box-shadow: none !important;
  background: transparent !important;
}
.float-drawer {
    margin: 30px!important;
    border-radius: 50px!important;
    width: 30vw !important;
    padding: 20px!important;
    height:calc(100vh - 120px)!important;
}
.v-navigation-drawer--temporary.float-drawer{
  transform: translateX(650px)!important;
}
.v-navigation-drawer--active.float-drawer{
  transform: translateX(0px)!important;
}
.v-navigation-drawer__scrim{
  opacity: 0.5!important;
}
</style>
